import {useState} from "react";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ExploreIcon from "@mui/icons-material/Explore";
import LayersIcon from "@mui/icons-material/Layers";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {Navigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";

// --- Overlay control styles ---

const overlayControlSx = {
    backgroundColor: "rgba(9, 25, 32, 0.85)",
    border: "1px solid rgba(112, 147, 160, 0.35)",
    borderRadius: "8px",
    backdropFilter: "blur(8px)",
};

const overlayButtonSx = {
    color: "#7093a0",
    width: 36,
    height: 36,
    "&:hover": {
        color: "#02cfee",
        backgroundColor: "rgba(2, 207, 238, 0.08)",
    },
};

// --- Zoom Control overlay ---

function ZoomControl({onZoomIn, onZoomOut, onFit}: {
    onZoomIn: () => void;
    onZoomOut: () => void;
    onFit: () => void;
}) {
    return (
        <Stack sx={overlayControlSx} spacing={0}>
            <Tooltip title="Zoom in" placement="right">
                <IconButton size="small" onClick={onZoomIn} sx={overlayButtonSx}>
                    <AddIcon sx={{fontSize: 20}} />
                </IconButton>
            </Tooltip>
            <Box sx={{height: "1px", backgroundColor: "rgba(112, 147, 160, 0.25)", mx: "6px"}} />
            <Tooltip title="Zoom out" placement="right">
                <IconButton size="small" onClick={onZoomOut} sx={overlayButtonSx}>
                    <RemoveIcon sx={{fontSize: 20}} />
                </IconButton>
            </Tooltip>
            <Box sx={{height: "1px", backgroundColor: "rgba(112, 147, 160, 0.25)", mx: "6px"}} />
            <Tooltip title="Fit to view" placement="right">
                <IconButton size="small" onClick={onFit} sx={overlayButtonSx}>
                    <MyLocationIcon sx={{fontSize: 18}} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

// --- Compass Control overlay ---

function CompassControl({bearing, onResetBearing}: {
    bearing: number;
    onResetBearing: () => void;
}) {
    return (
        <Stack sx={overlayControlSx} alignItems="center" justifyContent="center">
            <Tooltip title="Reset bearing" placement="right">
                <IconButton size="small" onClick={onResetBearing} sx={overlayButtonSx}>
                    <ExploreIcon sx={{fontSize: 22, transform: `rotate(${-bearing}deg)`, transition: "transform 0.3s ease"}} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

// --- Layer toggle overlay ---

function LayerControl({active, onToggle}: {
    active: boolean;
    onToggle: () => void;
}) {
    return (
        <Stack sx={overlayControlSx}>
            <Tooltip title="Map layers" placement="left">
                <IconButton size="small" onClick={onToggle}
                    sx={{
                        ...overlayButtonSx,
                        ...(active ? {color: "#02cfee", backgroundColor: "rgba(2, 207, 238, 0.1)"} : {}),
                    }}>
                    <LayersIcon sx={{fontSize: 20}} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

// --- Filter overlay panel (collapsible) ---

function FilterPanel({visible, onClose}: {visible: boolean; onClose: () => void}) {
    if (!visible) return null;
    return (
        <Box sx={{
            ...overlayControlSx,
            width: 260,
            padding: "12px",
        }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{fontSize: "0.85rem", fontWeight: 700, color: "#F2F5FA"}}>Location Filter</Typography>
                    <IconButton size="small" onClick={onClose} sx={{color: "#7093a0", "&:hover": {color: "#d06440"}}}>
                        <Typography sx={{fontSize: "0.75rem", fontWeight: 700}}>X</Typography>
                    </IconButton>
                </Stack>
                <Box sx={{height: "1px", backgroundColor: "#3F464F"}} />
                {/* TODO: Bounding box filter, type filter, date range */}
                <Typography sx={{fontSize: "0.7rem", color: "#7093a0", fontStyle: "italic", textAlign: "center"}}>
                    Filter controls must be configured
                </Typography>
            </Stack>
        </Box>
    );
}

// --- Details overlay panel (collapsible) ---

function DetailsPanel({visible, onClose}: {visible: boolean; onClose: () => void}) {
    if (!visible) return null;
    return (
        <Box sx={{
            ...overlayControlSx,
            width: 300,
            maxHeight: 400,
            padding: "12px",
            overflowY: "auto",
        }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{fontSize: "0.85rem", fontWeight: 700, color: "#F2F5FA"}}>Location Details</Typography>
                    <IconButton size="small" onClick={onClose} sx={{color: "#7093a0", "&:hover": {color: "#d06440"}}}>
                        <Typography sx={{fontSize: "0.75rem", fontWeight: 700}}>X</Typography>
                    </IconButton>
                </Stack>
                <Box sx={{height: "1px", backgroundColor: "#3F464F"}} />
                {/* TODO: Selected location details, coordinates, linked intel/resources */}
                <Typography sx={{fontSize: "0.7rem", color: "#7093a0", fontStyle: "italic", textAlign: "center"}}>
                    Select a location marker to view details.
                </Typography>
            </Stack>
        </Box>
    );
}

// --- Main component ---

function UXLocations() {
    const {networkUID} = useParams();
    const [bearing, setBearing] = useState(0);
    const [showFilter, setShowFilter] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [showLayers, setShowLayers] = useState(false);

    // TODO: These will control the map component when integrated
    const handleZoomIn = () => { /* TODO: map.zoomIn() */ };
    const handleZoomOut = () => { /* TODO: map.zoomOut() */ };
    const handleFit = () => { /* TODO: map.fitBounds() */ };
    const handleResetBearing = () => { setBearing(0); /* TODO: map.resetBearing() */ };

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Locations"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Locations", linkTo: "", activeView: true},
                        ]}
                    />
                    {/* Map container — full remaining height */}
                    <Box sx={{
                        ...appStyles.sectionBox,
                        flex: 1,
                        minHeight: 500,
                        position: "relative",
                        overflow: "hidden",
                        padding: 0,
                    }}>
                        {/* Map canvas area */}
                        <Box sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(0, 10, 20, 0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            {/* TODO: Map component goes here (Google Maps / Mapbox / etc.) */}
                            <Stack spacing={1} alignItems="center" sx={{opacity: 0.4}}>
                                <Box component="img" src="/images/locations.svg"
                                     sx={{width: 72, height: 72, opacity: 0.3}}
                                     onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; }}
                                />
                                <Typography sx={{fontSize: "0.85rem", color: "#7093a0"}}>
                                    Map is unable to render.  Check configuration
                                </Typography>
                            </Stack>
                        </Box>

                        {/* --- Overlay controls --- */}

                        {/* Top-left: Zoom + Compass */}
                        <Stack spacing={1} sx={{position: "absolute", top: 12, left: 12, zIndex: 10}}>
                            <ZoomControl onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onFit={handleFit} />
                            <CompassControl bearing={bearing} onResetBearing={handleResetBearing} />
                        </Stack>

                        {/* Top-right: Layer toggle + Filter toggle + Details toggle */}
                        <Stack spacing={1} sx={{position: "absolute", top: 12, right: 12, zIndex: 10, alignItems: "flex-end"}}>
                            <Stack direction="row" spacing={1}>
                                <Stack sx={overlayControlSx}>
                                    <Tooltip title="Filter locations" placement="left">
                                        <IconButton size="small" onClick={() => setShowFilter(f => !f)}
                                            sx={{
                                                ...overlayButtonSx,
                                                ...(showFilter ? {color: "#02cfee", backgroundColor: "rgba(2, 207, 238, 0.1)"} : {}),
                                            }}>
                                            <FilterListIcon sx={{fontSize: 20}} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <LayerControl active={showLayers} onToggle={() => setShowLayers(l => !l)} />
                            </Stack>
                            <FilterPanel visible={showFilter} onClose={() => setShowFilter(false)} />
                        </Stack>

                        {/* Bottom-right: Details panel */}
                        <Stack spacing={1} sx={{position: "absolute", bottom: 12, right: 12, zIndex: 10, alignItems: "flex-end"}}>
                            <Stack direction="row" spacing={1} alignItems="flex-end">
                                {!showDetails && (
                                    <Stack sx={overlayControlSx}>
                                        <Tooltip title="Location details" placement="left">
                                            <IconButton size="small" onClick={() => setShowDetails(true)} sx={overlayButtonSx}>
                                                <InfoOutlinedIcon sx={{fontSize: 20}} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </Stack>
                            <DetailsPanel visible={showDetails} onClose={() => setShowDetails(false)} />
                        </Stack>

                        {/* Bottom-left: Coordinates display */}
                        <Box sx={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            zIndex: 10,
                            ...overlayControlSx,
                            padding: "4px 10px",
                        }}>
                            <Typography sx={{fontSize: "0.65rem", color: "#7093a0", fontFamily: "monospace"}}>
                                0.0000°, 0.0000° &nbsp;|&nbsp; Zoom: 4
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            )}
        </Stack>
    );
}

export default UXLocations;
